/**
 * What the car's own mode words mean to a guest.
 *
 * `LiftViewModel.mode` cannot answer "is this lift working for guests":
 * the connector folds every staff and out-of-service setting into
 * `human` (`vocab.py::mode_class`), so a car on Independent Service, one
 * on Inspection and one in Normal Operation all read `human`, and one
 * switched off reads `human` too. The 2026-09-16 closure was a car on
 * Independent Service that every screen called a working lift.
 *
 * The connector also carries the controller's own words through as
 * `session.mode_label` -- the Schindler edge's `label_for(value,
 * LIFT_MODE)`, copied into `tests/fixtures/schindler-lift-modes.json`
 * from adapters-lift `edge/src/nexsoss_bridge/domain/mappings.py`. This
 * file reads THAT, and only that.
 *
 * EXACT MATCH ONLY. A label is read as its class only when it is
 * byte-identical to one in the table: "normal operation", a trailing
 * space or a future vendor rewording all read `unrecognised`, and are
 * quoted rather than interpreted. The one reading that must never be
 * wrong is "Working normally", and it belongs to "Normal Operation"
 * alone -- a car whose words this file does not know is not a car it
 * may call normal.
 */

export type LiftModeClass =
  /** "Normal Operation": the only mode in which guests are served as designed. */
  | 'normal'
  /** The car is being, or has been, handed to the AGV system (97, 98). */
  | 'robot'
  /** Fire service has the car (the connector's FIRE_MODES, 7 and 8). */
  | 'fire'
  /** A staff or technician setting, usually a key switch. */
  | 'staff'
  /** Switched off, faulted, stopped by a button, overloaded, or on
   *  emergency power: guests cannot rely on it. */
  | 'not_in_service'
  /** No mode reported: value 0 ("undefined"), an empty string, or no
   *  label at all (an adapter older than `mode_label`). */
  | 'not_reported'
  /** Any other string, including the edge's `unknown(<value>)` for a
   *  value its table lacks. Quoted, never interpreted. */
  | 'unrecognised';

export interface LiftModeLabelReading {
  readonly modeClass: LiftModeClass;
  /** The car's own words, verbatim, when it said any. Null for
   *  `not_reported`. */
  readonly label: string | null;
}

/**
 * The Schindler LiftMode labels, keyed by the exact string the edge
 * publishes. `lift-mode-label.test.ts` holds this equal to the fixture
 * in both directions, so a label added upstream (and copied into the
 * fixture by the drift check) fails until someone decides its class.
 */
export const SCHINDLER_LIFT_MODE_CLASS: Readonly<Record<string, LiftModeClass>> = Object.freeze({
  undefined: 'not_reported',
  'Normal Operation': 'normal',
  'Switch off, out of order': 'not_in_service',
  'Independent Service, elevator reserved': 'staff',
  'Priority Trip': 'staff',
  'Attendant Service': 'staff',
  'Fire Emergency': 'fire',
  'Fire Fighter Service': 'fire',
  'Emergency Power': 'not_in_service',
  Inspection: 'staff',
  Overload: 'not_in_service',
  'Emergency Power Operation': 'not_in_service',
  'Elevator Not Available': 'not_in_service',
  'Error General': 'not_in_service',
  'Test Trip': 'staff',
  'Emergency Stop forced by switch in the car': 'not_in_service',
  'Emergency Trip served by the specified elevator': 'not_in_service',
  'Emergency Trip served by best elevator of group': 'not_in_service',
  'Cleaner Control': 'staff',
  'VIP Trip': 'staff',
  'Over Temperature MR, machine room': 'not_in_service',
  'Switch off by Servitel, out of order by remote system': 'not_in_service',
  'Emergency Stop by Push-Button': 'not_in_service',
  'Alarm Button Pressed': 'not_in_service',
  'Acceptances Test Control': 'staff',
  'Technical OFF': 'not_in_service',
  'AGV1 - AGV Request': 'robot',
  'AGV2 - AGV Automatic, elevator reserved for AGV system': 'robot',
  'No Communication to Controller': 'not_in_service',
  'Error Elevator Control': 'not_in_service',
  'Error Load Measurement System': 'not_in_service',
  'Error Door Control': 'not_in_service',
  'Error Drive Control': 'not_in_service',
  'Emergency Power Evacuation': 'not_in_service',
  'Emergency Power Evacuation done': 'not_in_service',
});

/** The one label that reads "Working normally". */
export const NORMAL_OPERATION_LABEL = 'Normal Operation';

/**
 * Read the car's mode words. `hasOwnProperty`, not `in` or a plain
 * index: "constructor" or "toString" must not find a class on the
 * object's prototype and come back as something other than
 * `unrecognised`.
 */
export function readLiftModeLabel(label: string | null | undefined): LiftModeLabelReading {
  if (label == null || label === '') return { modeClass: 'not_reported', label: null };
  const modeClass = Object.prototype.hasOwnProperty.call(SCHINDLER_LIFT_MODE_CLASS, label)
    ? SCHINDLER_LIFT_MODE_CLASS[label]
    : undefined;
  if (modeClass === undefined) return { modeClass: 'unrecognised', label };
  return { modeClass, label: modeClass === 'not_reported' ? null : label };
}

/**
 * The Guests line a mode label gives on its own, before anything about
 * a robot session is said.
 *
 * Every class but `normal` and `not_reported` QUOTES the car: the words
 * are the controller's, and an FOH reader can read them to a lift
 * engineer on the phone. "Working normally" carries its source too, so
 * nobody mistakes it for something this screen measured.
 */
export function liftModeGuestsLine(reading: LiftModeLabelReading): string {
  const quoted = reading.label === null ? '' : ` (“${reading.label}”)`;
  switch (reading.modeClass) {
    case 'normal':
      return 'Working normally (car says Normal Operation)';
    case 'robot':
      return `Set aside for robots${quoted}`;
    case 'fire':
      return 'Fire service';
    case 'staff':
      return `On a staff setting${quoted}`;
    case 'not_in_service':
      return `Not in normal service${quoted}`;
    case 'not_reported':
      return 'Not known: the car’s setting is not reported';
    case 'unrecognised':
      return `Not known: the car reports “${reading.label ?? ''}”`;
  }
}
